import { ShieldAlert } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { ROUTES } from "@/app/routes";
import { useSession } from "@/core/auth/session";
import { Button } from "@/ui/button";
import { Spinner } from "@/ui/spinner";

import { SignOutEverywhereDialog } from "./SignOutEverywhereDialog";

function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center bg-bg p-6">{children}</div>;
}

/**
 * The portal's gate: signed out → sign-in (and back here after); signed in without an admin role → a clear
 * "not for you" screen. The API enforces the same on every call; this only spares people the errors.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const [leaving, setLeaving] = useState(false);
  const [everywhere, setEverywhere] = useState(false);

  if (session.status === "loading") {
    return (
      <FullScreen>
        <Spinner label="Checking your session…" />
      </FullScreen>
    );
  }
  if (session.status === "unavailable") {
    return (
      <FullScreen>
        <div role="alert" className="flex max-w-sm flex-col items-center gap-4 text-center">
          <p className="text-lg font-bold">Can't reach Brocoly</p>
          <p className="text-sm text-muted">Check your connection. Your work isn't lost.</p>
          <Button onClick={session.retry}>Try again</Button>
        </div>
      </FullScreen>
    );
  }
  if (session.status === "signed-out") {
    const next = location.pathname + location.search;
    const target =
      next === ROUTES.overview ? ROUTES.signIn : `${ROUTES.signIn}?next=${encodeURIComponent(next)}`;
    return <Navigate to={target} replace />;
  }
  if (!session.isAdmin) {
    return (
      <FullScreen>
        <div className="flex max-w-md flex-col items-center gap-4 rounded-2xl border border-border bg-surface p-8 text-center">
          <ShieldAlert className="size-10 text-warn" aria-hidden="true" />
          <h1 className="text-xl font-extrabold">This portal is for the Brocoly team</h1>
          <p className="text-sm text-muted">
            {session.me?.email ?? "This account"} isn't an admin. If you run a kitchen, use the partner
            portal; if you should have access, ask a super admin.
          </p>
          <Button
            variant="secondary"
            loading={leaving}
            onClick={() => {
              setLeaving(true);
              void session.signOut().finally(() => setLeaving(false));
            }}
          >
            Sign out
          </Button>
          <Button
            variant="quiet"
            size="sm"
            onClick={() => setEverywhere(true)}
            // Quieter than Sign out: the stronger action (every device) shouldn't be the most inviting one.
            className="font-medium text-muted hover:bg-transparent hover:text-ink hover:underline"
          >
            Sign out everywhere
          </Button>
        </div>
        {everywhere && <SignOutEverywhereDialog onClose={() => setEverywhere(false)} />}
      </FullScreen>
    );
  }
  return children;
}
